import { Address, ApiLink, Revision, RevisionBy, RevisionEmbed, UUID } from '@mkp/shared/models';
import { LocationDto } from '@mkp/location/models';

interface CompanyProfileLinks {
  createdBy: ApiLink<'GET'>;
  self: ApiLink<'GET'>;
  update?: ApiLink<'GET'>;
  logo?: ApiLink<'GET'>;
  companyProfileHeaderImage?: ApiLink<'GET'>;
}

export interface CompanyProfileDescription {
  de: string | null;
  en: string | null;
  fr: string | null;
}

// The Core API accepts these two values only and rejects anything else. The social platform
// (LinkedIn, Facebook, …) is deliberately not encoded here: it is carried by the url alone and
// resolved downstream by Datapool, which maps `social` to its SOCIAL link type.
export enum CompanyProfileLinkType {
  Website = 'companyWebsite',
  Social = 'social',
}

export interface CompanyProfileLink {
  type: CompanyProfileLinkType;
  // Nullable: the form holds an empty website row for companies that have not set one, and a
  // freshly added social row before the user types. Empty urls are stripped before save.
  url: string | null;
}

export interface CompanyProfileLocation extends Revision, RevisionEmbed {
  companyProfileId: string;
  address: Address;
}

export interface CompanyProfileVideo {
  weight: number;
  url: string;
  description?: string;
}

export interface CompanyProfileEmployerBrandingDto {
  start: string;
}

export interface CompanyProfileBase {
  id: UUID;
  name: string;
  legalEntityId: UUID;
  locationId?: UUID;
  headerMediaApiId: UUID | null;
  logoMediaApiId: UUID | null;
  description: CompanyProfileDescription;
  employeeCountFrom: number;
  employeeCountTo: number;
  benefits: UUID[];
  videos: CompanyProfileVideo[];
  employerBranding: CompanyProfileEmployerBrandingDto | null;
  links: CompanyProfileLink[];
  _links?: CompanyProfileLinks;
}

export interface CompanyProfileExtras extends Revision {
  _embedded: {
    createdBy: Readonly<RevisionBy>;
    updatedBy: Readonly<RevisionBy | null>;
    locations: LocationDto[];
  };
}

export type CompanyProfileDto = CompanyProfileBase & Readonly<CompanyProfileExtras>;

export interface EnhancedCompanyProfileDto extends CompanyProfileDto {
  legalEntityName: string;
  legalEntityAddress: Address;
  legalEntityState: string;
  legalEntityVersion?: string;
}

export type CompanyProfilePostBody = Omit<CompanyProfileBase, 'id' | 'description' | 'links'> & {
  description: CompanyProfileBase['description'] | null;
  links: CompanyProfileBase['links'] | null;
};
